import { ActionMenu, Badge, Checkbox, IconButton, Table } from '@ntconsult/aluma-react';
import { Eye, MoreVertical, Pencil, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AllocationColumn } from '../hooks/useAllocationColumns';
import type { Allocation, ProfessionalWithAllocations } from '../types/Allocation';
import { formatDisplayDate } from '../utils/data';
import { contractLabel, professionalLabel } from '../utils/labels';
import { formatMoney, previewEbitdaPercentage } from '../utils/money';
import type { EditAllocationTarget } from './AllocationModal';
import type { CloseAllocationTarget } from './CloseAllocationModal';
import { TagBadge } from './TagBadge';
import { STICKY_END_CELL } from './wideTable';
import { YesNoBadge } from './YesNoBadge';

type AllocationRowProps = {
  professional: Omit<ProfessionalWithAllocations, 'allocations'>;
  allocation: Allocation;
  /** Multialocados: o profissional numa célula só (`rowSpan`) na primeira linha do grupo. */
  group?: { first: boolean; size: number; separated: boolean };
  isVisible: (column: AllocationColumn) => boolean;
  selection?: { selected: boolean; onChange: (checked: boolean) => void };
  onView: (target: EditAllocationTarget) => void;
  onEdit: (target: EditAllocationTarget) => void;
  onCloseRequest: (target: CloseAllocationTarget) => void;
};

function ebitdaPercentageLabel(
  allocation: Pick<Allocation, 'monthlyNetRevenue' | 'allocatedCost' | 'billingCurrency'>,
  locale: string,
): string {
  if (!allocation.billingCurrency) return '';
  const value = previewEbitdaPercentage(allocation.monthlyNetRevenue, allocation.allocatedCost);
  if (value === null) return '';
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

/** Regra 12 (`02-spec-tela-design.md`): `BR 100`, `US 100` ou `BR 70 · US 30`. */
function costShareLabel(
  { costShareBr, costShareUs }: Pick<Allocation, 'costShareBr' | 'costShareUs'>,
  br: string,
  us: string,
): string {
  if (costShareUs === 0) return `${br} ${costShareBr}`;
  if (costShareBr === 0) return `${us} ${costShareUs}`;
  return `${br} ${costShareBr} · ${us} ${costShareUs}`;
}

/**
 * Uma linha por alocação, com o profissional repetido na primeira coluna
 * (reunião de 22/10, como no Access 2). `data-professional` é o que os e2e
 * usam para separar essa célula das colunas da alocação.
 */
export function AllocationRow({
  professional,
  allocation,
  group,
  isVisible,
  selection,
  onView,
  onEdit,
  onCloseRequest,
}: AllocationRowProps) {
  const { t, i18n } = useTranslation(['alocacao', 'common']);
  const editTarget: EditAllocationTarget = {
    allocation,
    professional: {
      id: professional.professionalId,
      registrationNumber: professional.registrationNumber,
      name: professional.name,
    },
  };

  return (
    <Table.Row
      selected={selection?.selected}
      className={group?.first && group.separated ? 'border-t-2 border-t-border-strong' : undefined}
    >
      {selection && (
        <Table.Cell className="w-px">
          <Checkbox
            checked={selection.selected}
            onCheckedChange={selection.onChange}
            label={
              <span className="sr-only">
                {t('alocacao:lote.selecionarAlocacao', {
                  projeto: `${allocation.project.axisCode} - ${allocation.project.name}`,
                })}
              </span>
            }
          />
        </Table.Cell>
      )}
      {(!group || group.first) && (
        <Table.Cell
          rowSpan={group?.size}
          className={group ? 'align-top font-medium' : 'font-medium'}
          data-professional=""
        >
          {professionalLabel(professional)}
        </Table.Cell>
      )}
      {isVisible('project') && (
        <Table.Cell>
          {allocation.project.axisCode} - {allocation.project.name}
        </Table.Cell>
      )}
      {isVisible('contract') && <Table.Cell>{contractLabel(allocation.contract)}</Table.Cell>}
      {isVisible('client') && <Table.Cell>{allocation.client.name}</Table.Cell>}
      {isVisible('unit') && <Table.Cell align="center">{allocation.unit.code}</Table.Cell>}
      {isVisible('allocatedPercentage') && (
        <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
          {allocation.allocatedPercentage}%
        </Table.Cell>
      )}
      {isVisible('billable') && (
        <Table.Cell align="center">
          <YesNoBadge value={allocation.billable} />
        </Table.Cell>
      )}
      {isVisible('costShare') && (
        <Table.Cell className="whitespace-nowrap tabular-nums">
          {costShareLabel(allocation, t('alocacao:tabela.br'), t('alocacao:tabela.us'))}
        </Table.Cell>
      )}
      {isVisible('salesProfile') && <Table.Cell>{allocation.salesProfile?.name}</Table.Cell>}
      {isVisible('monthlyCost') && (
        <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
          {formatMoney(allocation.monthlyCost, allocation.costCurrency?.iso, i18n.language)}
        </Table.Cell>
      )}
      {isVisible('allocatedCost') && (
        <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
          {formatMoney(allocation.allocatedCost, allocation.costCurrency?.iso, i18n.language)}
        </Table.Cell>
      )}
      {isVisible('monthlyGrossRevenue') && (
        <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
          {/* Sem moeda de preço não há perfil/valor carregado: o 0 default do banco não é faturamento. */}
          {allocation.billingCurrency &&
            formatMoney(
              allocation.monthlyGrossRevenue,
              allocation.billingCurrency.iso,
              i18n.language,
            )}
        </Table.Cell>
      )}
      {isVisible('monthlyNetRevenue') && (
        <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
          {allocation.billingCurrency &&
            formatMoney(
              allocation.monthlyNetRevenue,
              allocation.billingCurrency.iso,
              i18n.language,
            )}
        </Table.Cell>
      )}
      {isVisible('ebitdaPercentage') && (
        <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
          {ebitdaPercentageLabel(allocation, i18n.language)}
        </Table.Cell>
      )}
      {isVisible('startDate') && (
        <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
          {formatDisplayDate(allocation.startDate, i18n.language)}
        </Table.Cell>
      )}
      {isVisible('endDate') && (
        <Table.Cell align="right" className="whitespace-nowrap tabular-nums">
          {allocation.endDate ? (
            <span className="inline-flex items-center gap-1.5">
              {/* Reunião de 15/09: fim estimado ≠ confirmado (projeção financeira). */}
              {!allocation.fimConfirmado && (
                <Badge variant="soft" tone="neutral" size="sm">
                  {t('alocacao:tabela.fimEstimado')}
                </Badge>
              )}
              {formatDisplayDate(allocation.endDate, i18n.language)}
            </span>
          ) : (
            t('alocacao:tabela.semTermino')
          )}
        </Table.Cell>
      )}
      {isVisible('notes') && (
        <Table.Cell className="max-w-xs whitespace-pre-line">{allocation.notes || '—'}</Table.Cell>
      )}
      {isVisible('tags') && (
        <Table.Cell>
          <div className="flex flex-wrap gap-1">
            {allocation.tags.map((tag) => (
              <TagBadge key={tag.id} tag={tag} />
            ))}
          </div>
        </Table.Cell>
      )}
      {isVisible('accountDirector') && (
        <Table.Cell>{allocation.project.accountDirectorName || '—'}</Table.Cell>
      )}
      {isVisible('operationsManager') && (
        <Table.Cell>{allocation.project.operationsManagerName || '—'}</Table.Cell>
      )}
      {isVisible('projectManager') && (
        <Table.Cell>{allocation.project.projectManagerName || '—'}</Table.Cell>
      )}
      <Table.Cell align="right" className={STICKY_END_CELL}>
        <ActionMenu
          align="right"
          trigger={
            <IconButton
              variant="icon"
              size="sm"
              icon={<MoreVertical size={16} />}
              label={t('alocacao:tabela.acoesDaAlocacao', {
                projeto: `${allocation.project.axisCode} - ${allocation.project.name}`,
              })}
            />
          }
        >
          <ActionMenu.Item icon={<Eye size={16} />} onClick={() => onView(editTarget)}>
            {t('alocacao:acoes.visualizar')}
          </ActionMenu.Item>
          <ActionMenu.Item icon={<Pencil size={16} />} onClick={() => onEdit(editTarget)}>
            {t('alocacao:acoes.editar')}
          </ActionMenu.Item>
          {allocation.status !== 'closed' && !allocation.fimConfirmado && (
            <ActionMenu.Item
              variant="danger"
              icon={<XCircle size={16} />}
              onClick={() =>
                onCloseRequest({
                  allocation,
                  professional: {
                    registrationNumber: professional.registrationNumber,
                    name: professional.name,
                  },
                })
              }
            >
              {t('alocacao:acoes.encerrar')}
            </ActionMenu.Item>
          )}
        </ActionMenu>
      </Table.Cell>
    </Table.Row>
  );
}
